import { useEffect, useRef, useCallback } from 'react';

// Own the still-photo stream even while permission/frame requests are pending.
export default function usePhotoCamera({ active, sessionId, facingMode, quality, videoRef, onReady }) {
  const stopRef = useRef(() => {});
  const stop = useCallback(() => stopRef.current(), []);

  useEffect(() => {
    if (!active) return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    let stream;
    let frameCallback;
    let animationFrame;
    onReady(false);

    const cleanup = () => {
      if (cancelled) return;
      cancelled = true;
      video.onloadeddata = null;
      if (frameCallback !== undefined) video.cancelVideoFrameCallback?.(frameCallback);
      if (animationFrame !== undefined) cancelAnimationFrame(animationFrame);
      stream?.getTracks().forEach(track => track.stop());
      if (video.srcObject === stream) video.srcObject = null;
    };
    stopRef.current = cleanup;

    const ready = () => {
      if (cancelled) return;
      if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
        onReady(true);
      } else {
        animationFrame = requestAnimationFrame(ready);
      }
    };
    const waitForFrame = () => {
      if (cancelled) return;
      if (video.requestVideoFrameCallback) {
        frameCallback = video.requestVideoFrameCallback(ready);
      } else {
        ready();
      }
    };

    async function acquire() {
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: quality === 'hd' ? 1920 : 1280 },
              height: { ideal: quality === 'hd' ? 1080 : 720 },
              facingMode: { ideal: facingMode }
            },
            audio: false
          });
        } catch (error) {
          if (cancelled) return;
          stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
            audio: false
          });
        }
        if (cancelled) {
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        video.onloadeddata = waitForFrame;
        video.srcObject = stream;
        if (video.readyState >= 2) waitForFrame();
      } catch (error) {
        if (!cancelled) console.error('[CAMERA] unable to start photo camera', error);
        cleanup();
      }
    }
    acquire();
    return cleanup;
  }, [active, sessionId, facingMode, quality, videoRef, onReady]);

  return stop;
}
